import { memo } from "react";
import { CCol, CInput } from "@coreui/react";
import NumberFormat from "react-number-format";
import { ceilToDecimalPlaces } from "src/utils/ceilToDecimalPlaces";
import { PhaseCard, TaskItem } from "src/reusable";
import { ItemBadge, PhaseProgress } from "./ProjectTask";
import "./ProjectDetails.css";

const PowAmountInput = ({ task, taskIndex, phaseIndex, isSaveDisabled, onPowAmountChange }) => {
  const powAmount = task.pow_amount ?? 0;
  const originalIndex = task._originalTaskIndex ?? taskIndex;

  const handleFocus = () => {
    if (powAmount !== "" && Number(powAmount) === 0) {
      if (typeof onPowAmountChange === "function") {
        onPowAmountChange("", originalIndex, phaseIndex);
      }
    }
  };

  const handleBlur = (e) => {
    if (e.target.value === "" || e.target.value === null) {
      if (typeof onPowAmountChange === "function") {
        onPowAmountChange(0, originalIndex, phaseIndex);
      }
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    if (typeof onPowAmountChange === "function") {
      onPowAmountChange(value, originalIndex, phaseIndex);
    }
  };

  return (
    <div className="flex w-full flex-col items-center justify-start px-2">
      <span className="text-[10px] text-gray-500 font-medium leading-tight block mb-1">
        POW Amount
      </span>

      <div className="w-full max-w-[190px]">
        <CInput
          type="number"
          min="0"
          step="0.01"
          value={powAmount}
          disabled={isSaveDisabled}
          placeholder="0"
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          className={`w-full rounded-md border ${Number(powAmount) === 0 ? "border-aimhi-warning" : "border-gray-300"} bg-white py-2 pl-3 pr-3 text-right text-sm font-medium text-gray-800 shadow-sm outline-none transition disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400`}
        />
      </div>
    </div>
  );
};

export const ProjectPowAmount = memo(({
  phaseItem,
  phaseIndex,
  isExpanded,
  isSaveDisabled = false,
  onTogglePhase,
  onPowAmountChange,
}) => {
  if (!phaseItem) return null;

  const totalBudget = ceilToDecimalPlaces(phaseItem.Total_Budget, 2);
  const totalCost = ceilToDecimalPlaces(phaseItem.Total_POW_Amount, 2);
  const progressPercentage = ceilToDecimalPlaces(phaseItem.ProgressPercentage, 2);
  const isZeroPow = (phaseItem.Total_POW_Amount ?? 0) === 0;

  const phaseMetrics = [
    {
      id: "badges",
      xs: "12",
      sm: "12",
      md: "4",
      lg: "3",
      value: (
        <div className="flex flex-row flex-wrap md:flex-col items-center md:items-center justify-start gap-1.5 my-1 md:my-0">
          <ItemBadge
            underBudget={phaseItem.under_budget}
            delayed={phaseItem.delayed}
            incomplete={phaseItem.incomplete}
          />
        </div>
      ),
    },
    {
      id: "total_estimate",
      xs: "6",
      sm: "6",
      md: "4",
      lg: "4",
      label: "Total Construction Estimate",
      align: "left",
      value: (
        <div className="text-sm md:text-base font-bold text-[#23303d] mt-1 whitespace-nowrap">
          ₱
          <NumberFormat
            value={totalBudget}
            displayType="text"
            thousandSeparator
            decimalScale={2}
            fixedDecimalScale
            style={{ marginLeft: "2px" }}
          />
        </div>
      ),
    },
    {
      id: "total_pow",
      xs: "6",
      sm: "6",
      md: "4",
      lg: "5",
      label: "Total POW Amount",
      align: "right",
      value: (
        <div className="text-sm md:text-base font-bold text-[#23303d] mt-1 whitespace-nowrap">
          ₱
          <NumberFormat
            value={totalCost}
            displayType="text"
            thousandSeparator
            decimalScale={2}
            fixedDecimalScale
            style={{ marginLeft: "2px" }}
          />
        </div>
      ),
    },
  ];

  return (
    <CCol
      xs="12"
      sm="12"
      md="12"
      lg="12"
      style={{
        padding: "1px",
        marginTop: "20px",
      }}
    >
      <PhaseCard
        phaseItem={phaseItem}
        phaseIndex={phaseIndex}
        onTogglePhase={onTogglePhase}
        cardClassName={isZeroPow ? "border-danger" : ""}
        cardStyle={isZeroPow ? { border: "1px solid #e53e3e" } : undefined}
        metrics={phaseMetrics}
        rightActions={<PhaseProgress percentage={progressPercentage} />}
      />

      <CCol xs="12" sm="12" md="12" lg="12" style={{ padding: "0px" }}>
        <div
          className={`transition-all duration-300 ${isExpanded ? "opacity-100" : "max-h-0 overflow-hidden opacity-0"
            }`}
        >
          {(phaseItem.Task || []).map((task, taskIndex) => {
            const taskKey = task.sow_id ?? task.id ?? taskIndex;

            const taskMetrics = [
              {
                id: "accomplishment",
                xs: "6",
                sm: "4",
                items: [
                  {
                    label: "Expected Accomplishment",
                    value: ceilToDecimalPlaces(task.expected_accomplishment, 2) || "---",
                  },
                  {
                    label: "Actual Accomplishment",
                    value: ceilToDecimalPlaces(task.actual_accomplishment, 2) || "---",
                  },
                ],
              },
              {
                id: "weight",
                xs: "6",
                sm: "4",
                items: [
                  {
                    label: "Expected Weight %",
                    value: task.expected_wt_percentage != null ? `${ceilToDecimalPlaces(task.expected_wt_percentage, 2)}%` : "---",
                  },
                  {
                    label: "Actual Weight %",
                    value: task.actual_wt_percentage != null ? `${ceilToDecimalPlaces(task.actual_wt_percentage, 2)}%` : "---",
                  },
                ],
              },
              {
                id: "estimated_cost",
                xs: "12",
                sm: "4",
                label: "Estimated Cost",
                value: (
                  <div className="text-sm md:text-base font-bold text-[#23303d] mt-1 whitespace-nowrap">
                    ₱
                    <NumberFormat
                      value={ceilToDecimalPlaces(task.estimate_amount ?? 0, 2)}
                      displayType="text"
                      thousandSeparator
                      decimalScale={2}
                      fixedDecimalScale
                      style={{ marginLeft: "2px" }}
                    />
                  </div>
                ),
              },
            ];

            return (
              <TaskItem
                key={`${taskKey}-${taskIndex}`}
                task={task}
                taskIndex={taskIndex}
                phaseIndex={phaseIndex}
                metrics={taskMetrics}
                rightActions={(task, { taskIndex: tIdx, phaseIndex: pIdx }) => (
                  <PowAmountInput
                    task={task}
                    taskIndex={tIdx}
                    phaseIndex={pIdx}
                    isSaveDisabled={isSaveDisabled}
                    onPowAmountChange={onPowAmountChange}
                  />
                )}
              />
            );
          })}
        </div>
      </CCol>
    </CCol>
  );
});

export default ProjectPowAmount;




